import React from 'react';
import { richFieldToPlainText, type RichField } from '@/types/rich-text';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import type { TextRoleStyle } from './semanticTextRoles';
import { ChartBarsSvg, type ChartBarView } from './ChartBarsSvg';

/**
 * WEB-447 P1-2b — chart block: decorative static inline SVG (no chart lib).
 * Shared between the public render engine and the editor canvas preview.
 * Two kinds: progress_projection (ascending curve toward the goal) and
 * comparison (two bars: start vs goal). Theme-aware via CSS variables.
 * WEB-3252: third kind `bars` (2..5 captioned bars) — drawn by ChartBarsSvg.
 */

const DEFAULT_ACCENT = 'var(--color-primary, #4094F7)';
const LABEL_COLOR = 'var(--color-text-secondary, #8c8c8c)';
const GRID_COLOR = 'var(--color-border-primary, #e8e8e8)';
const SURFACE_COLOR = 'var(--color-bg-surface, #fff)';

const VIEW_W = 320;
const VIEW_H = 170;

/** Extracts the first numeric token from a free-form value like "62 kg". */
function parseNumericValue(value?: string): number | null {
  if (!value) return null;
  const match = value.replace(',', '.').match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const parsed = parseFloat(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

interface ProjectionSvgProps {
  accent: string;
  gradientId: string;
  startValue?: string;
  endValue?: string;
  /** WEB-1692 юнит 8: роль `Secondary` — подписи шкалы и значение НАЧАЛА. */
  secondaryRole: TextRoleStyle;
}

function ProgressProjectionSvg({ accent, gradientId, startValue, endValue, secondaryRole }: ProjectionSvgProps): React.ReactElement {
  const curve = 'M 30 128 C 110 124, 190 96, 290 42';
  const area = `${curve} L 290 150 L 30 150 Z`;
  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: accent, stopOpacity: 0.28 }} />
          <stop offset="100%" style={{ stopColor: accent, stopOpacity: 0.02 }} />
        </linearGradient>
      </defs>
      {[46, 88, 130].map((y) => (
        <line
          key={y}
          x1={24}
          y1={y}
          x2={296}
          y2={y}
          style={{ stroke: GRID_COLOR }}
          strokeWidth={1}
          strokeDasharray="4 5"
        />
      ))}
      <path d={area} fill={`url(#${gradientId})`} />
      <path d={curve} style={{ stroke: accent }} strokeWidth={3.5} fill="none" strokeLinecap="round" />
      <circle cx={30} cy={128} r={5} style={{ fill: SURFACE_COLOR, stroke: accent }} strokeWidth={2.5} />
      <circle cx={290} cy={42} r={11} style={{ fill: accent, opacity: 0.18 }} />
      <circle cx={290} cy={42} r={5.5} style={{ fill: accent, stroke: SURFACE_COLOR }} strokeWidth={2} />
      {startValue && (
        <text
          x={30}
          y={110}
          textAnchor="start"
          fontSize={secondaryRole.fontSize ?? 13}
          fontWeight={600}
          style={{ fill: secondaryRole.color ?? LABEL_COLOR }}
        >
          {startValue}
        </text>
      )}
      {endValue && (
        <text x={290} y={24} textAnchor="end" fontSize={15} fontWeight={700} style={{ fill: accent }}>
          {endValue}
        </text>
      )}
    </svg>
  );
}

interface ComparisonSvgProps {
  accent: string;
  startLabel: string;
  endLabel: string;
  startValue?: string;
  endValue?: string;
  /**
   * WEB-1692 юнит 8: роль `Secondary`. ⚠ Здесь подписи живут ВНУТРИ рисунка, а
   * рисунок растянут по ширине экрана через viewBox — заданное число работает
   * как пропорция, а не как точные пиксели на телефоне. Про это честно сказано
   * в подсказке панели, выдумывать пересчёт мы не стали: он зависел бы от
   * ширины устройства и врал бы иначе.
   */
  secondaryRole: TextRoleStyle;
}

function ComparisonSvg({ accent, startLabel, endLabel, startValue, endValue, secondaryRole }: ComparisonSvgProps): React.ReactElement {
  const baseline = 136;
  const maxBar = 96;
  const minBar = 28;
  const startNum = parseNumericValue(startValue);
  const endNum = parseNumericValue(endValue);
  let startHeight = 52;
  let endHeight = maxBar;
  if (startNum !== null && endNum !== null && startNum > 0 && endNum > 0 && startNum !== endNum) {
    const maxValue = Math.max(startNum, endNum);
    startHeight = minBar + (startNum / maxValue) * (maxBar - minBar);
    endHeight = minBar + (endNum / maxValue) * (maxBar - minBar);
  }
  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      style={{ display: 'block' }}
    >
      <line x1={24} y1={baseline} x2={296} y2={baseline} style={{ stroke: GRID_COLOR }} strokeWidth={1.5} />
      <rect x={72} y={baseline - startHeight} width={64} height={startHeight} rx={6} style={{ fill: accent, opacity: 0.25 }} />
      <rect x={184} y={baseline - endHeight} width={64} height={endHeight} rx={6} style={{ fill: accent }} />
      {startValue && (
        <text
          x={104}
          y={baseline - startHeight - 10}
          textAnchor="middle"
          fontSize={secondaryRole.fontSize ?? 13}
          fontWeight={600}
          style={{ fill: secondaryRole.color ?? LABEL_COLOR }}
        >
          {startValue}
        </text>
      )}
      {endValue && (
        <text x={216} y={baseline - endHeight - 10} textAnchor="middle" fontSize={14} fontWeight={700} style={{ fill: accent }}>
          {endValue}
        </text>
      )}
      <text
        x={104}
        y={baseline + 22}
        textAnchor="middle"
        fontSize={secondaryRole.fontSize ?? 13}
        style={{ fill: secondaryRole.color ?? LABEL_COLOR }}
      >
        {startLabel}
      </text>
      <text
        x={216}
        y={baseline + 22}
        textAnchor="middle"
        fontSize={secondaryRole.fontSize ?? 13}
        style={{ fill: secondaryRole.color ?? LABEL_COLOR }}
      >
        {endLabel}
      </text>
    </svg>
  );
}

export interface ChartBlockSvgProps {
  /** id блока — нужен только для уникального id градиента внутри SVG. */
  blockId: string;
  kind?: 'progress_projection' | 'comparison' | 'bars';
  /** WEB-3252: столбцы вида `bars`; при других видах не рисуются. */
  bars?: readonly ChartBarView[];
  title?: RichField;
  startLabel?: string;
  endLabel?: string;
  startValue?: string;
  endValue?: string;
  accentColor?: string;
  /** Overrides the CSS variable fallback color for the optional title. */
  textColor?: string;
  /** Editor canvas preview: tighter margins and smaller title. */
  compact?: boolean;
  /**
   * WEB-785: BCP-47 funnel locale, used only to resolve the Today/Goal-style
   * fallback labels when the start/end labels are omitted by generation.
   * Omitted (e.g. editor canvas preview) → English fallback, matching prior
   * behavior.
   */
  locale?: string;
  /**
   * WEB-1692 юнит 8 — накладки смысловых ролей текста.
   *  · `titleRole` — заголовок графика;
   *  · `secondaryRole` — подписи шкалы и значение НАЧАЛА.
   * Значение ЦЕЛИ в роль не входит: оно несёт смысл рисунка и остаётся на
   * цвете акцента со своим кеглем (граница роли записана в реестре ролей).
   * Пусто (в том числе когда пропы не переданы вовсе) = сегодняшний вид
   * байт-в-байт: `??` возвращает прежние числа и переменные темы.
   */
  titleRole?: TextRoleStyle;
  secondaryRole?: TextRoleStyle;
}

/**
 * WEB-880 (добивка круга аудита 19-22.08): компонент СПЕЦИАЛЬНО принимает
 * готовые значения, а не блок целиком. У полей графика две номенклатуры —
 * короткая (её пишет генерация) и префиксная (её пишет кабинет); пару знает
 * реестр `ai-funnels/block-schema/field-name-registry.ts`, а чтение обеих
 * сторон живёт в ветке отрисовки рендерера — ровно там, где его видит
 * контракт-сторож имён полей (WEB-881). Пока чтение пряталось здесь, сторож
 * ветку графика не проверял, и блок годами рисовал умолчания вместо
 * заданных моделью подписей и чисел.
 */
export function ChartBlockSvg({
  blockId,
  kind,
  bars,
  title,
  startLabel,
  endLabel,
  startValue,
  endValue,
  accentColor,
  textColor,
  compact = false,
  locale,
  titleRole = {},
  secondaryRole = {},
}: ChartBlockSvgProps): React.ReactElement {
  const accent = accentColor || DEFAULT_ACCENT;
  // Вид не задан, а столбцов два и больше — «Столбцы»; то же правило у панели
  // (`properties/by-type/chart.tsx`, `effectiveChartKind`).
  const resolvedKind = kind ?? ((bars?.length ?? 0) >= 2 ? 'bars' : 'progress_projection');
  const localized = getPublicQuizStrings(locale);
  const resolvedStartLabel = startLabel || localized.chartStartLabel;
  const resolvedEndLabel = endLabel || localized.chartEndLabel;
  const gradientId = `qb-chart-grad-${blockId}`;

  return (
    <div style={{ marginBottom: compact ? 0 : 16 }}>
      {title && (
        <div
          style={{
            fontSize: titleRole.fontSize ?? (compact ? 13 : 17),
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: 8,
            color:
              titleRole.color ?? textColor ?? 'var(--color-text-primary, inherit)',
          }}
        >
          {richFieldToPlainText(title)}
        </div>
      )}
      {resolvedKind === 'bars' ? (
        // WEB-3252: меньше двух столбцов — только заголовок (ChartBarsSvg → null).
        <ChartBarsSvg accent={accent} bars={bars ?? []} secondaryRole={secondaryRole} />
      ) : resolvedKind === 'comparison' ? (
        <ComparisonSvg
          accent={accent}
          startLabel={resolvedStartLabel}
          endLabel={resolvedEndLabel}
          startValue={startValue}
          endValue={endValue}
          secondaryRole={secondaryRole}
        />
      ) : (
        <>
          <ProgressProjectionSvg
            accent={accent}
            gradientId={gradientId}
            startValue={startValue}
            endValue={endValue}
            secondaryRole={secondaryRole}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '4px 6px 0',
              fontSize: secondaryRole.fontSize ?? (compact ? 11 : 13),
              color: secondaryRole.color ?? LABEL_COLOR,
            }}
          >
            <span>{resolvedStartLabel}</span>
            <span>{resolvedEndLabel}</span>
          </div>
        </>
      )}
    </div>
  );
}
